import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Link,
  Alert,
  AlertIcon,
  FormErrorMessage,
} from '@chakra-ui/react';
import AuthLayout from '../components/auth/AuthLayout';
import { useAuthStore } from '../store/authStore';

const ResetPasswordPage = () => {
  const { resetPassword, loading } = useAuthStore();
  
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate email
    if (!validateEmail(email)) {
      setIsEmailInvalid(true);
      return;
    }

    try {
      await resetPassword(email);
      setSuccess(true);
      setEmail(''); // Clear the email field after success
    } catch (err: any) {
      setError(err.message || 'Failed to send reset password link');
    }
  };

  return (
    <AuthLayout 
      title="Reset your password" 
      subtitle="We'll send you a link to reset your password"
    >
      <form onSubmit={handleSubmit}>
        <Stack spacing={4}>
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          {success && (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              If an account exists with this email, you will receive a password reset link.
            </Alert>
          )}

          <FormControl id="email" isRequired isInvalid={isEmailInvalid}>
            <FormLabel>Email address</FormLabel>
            <Input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setIsEmailInvalid(false);
              }}
            />
            {isEmailInvalid && (
              <FormErrorMessage>Please enter a valid email address</FormErrorMessage>
            )}
          </FormControl>

          <Stack spacing={6}>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={loading}
              loadingText="Sending..."
              disabled={success}
            >
              Send reset link
            </Button>
          </Stack>

          <Stack pt={6}>
            <Link as={RouterLink} to="/login" color="blue.500" textAlign="center">
              Back to sign in
            </Link>
          </Stack>
        </Stack>
      </form>
    </AuthLayout>
  );
};

export default ResetPasswordPage;